import type { ColumnDef, Row } from "@tanstack/react-table";
import { t } from "i18next";

import type { ICvssVersion } from "./types";

import type { IOrganizationGroups } from "@types";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { getNonSelectableVulnerabilitiesOnReattackIds } from "features/vulnerabilities/utils";
import { formatLinkHandler } from "utils/format-helpers";
import { severityFormatter, tagsFormatter } from "utils/format-helpers";

const getSeverity = (cvssVersion: ICvssVersion, row: IVulnRowAttr): number => {
  if (cvssVersion.accessorKey === "severityThreatScore") {
    return row.severityThreatScore;
  }

  return row.severityTemporalScore;
};

const isValidVulnerability = (
  vulnerability: IVulnRowAttr,
  groups: IOrganizationGroups["groups"],
): boolean => {
  const firstFilteredVulnerabilities =
    getNonSelectableVulnerabilitiesOnReattackIds([vulnerability]);
  const filteredGroups = groups.filter(
    (group): boolean =>
      group.name.toLowerCase() === vulnerability.groupName.toLowerCase(),
  );

  if (filteredGroups.length === 0) {
    return false;
  }

  return (
    filteredGroups[0].serviceAttributes.includes("is_continuous") &&
    !firstFilteredVulnerabilities.includes(vulnerability.id) &&
    (filteredGroups[0].serviceAttributes.includes("has_advanced") ||
      vulnerability.source === "machine")
  );
};

const filteredContinuousVulnerabilitiesOnReattackIds = (
  vulnerabilities: IVulnRowAttr[],
  groups: IOrganizationGroups["groups"],
): string[] => {
  return vulnerabilities.reduce(
    (
      nonSelectableVulnerabilities: string[],
      vulnerability: IVulnRowAttr,
    ): string[] => {
      return isValidVulnerability(vulnerability, groups)
        ? [...nonSelectableVulnerabilities, vulnerability.id]
        : nonSelectableVulnerabilities;
    },
    [],
  );
};

const isRejectedOrSafeOrSubmitted = (row: Row<IVulnRowAttr>): boolean =>
  ["REJECTED", "SAFE", "SUBMITTED"].includes(row.original.state);

const isVerificationRequestedOrOnHold = (row: Row<IVulnRowAttr>): boolean =>
  ["requested", "on_hold"].includes(
    (row.original.verification ?? "").toLowerCase(),
  );

const getColumns = (cvssVersion: ICvssVersion): ColumnDef<IVulnRowAttr>[] => [
  {
    accessorKey: "organizationName",
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const link = `/orgs/${orgName}/groups/${cell.row.original.groupName}`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: "Organization",
    size: 70,
  },
  {
    accessorKey: "groupName",
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const link = `/orgs/${orgName}/groups/${cell.row.original.groupName}`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: t("organization.tabs.groups.newGroup.name.text"),
    size: 70,
  },
  {
    accessorFn: (row): string | undefined => row.finding?.title,
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const findingId =
        cell.row.original.finding === undefined
          ? ""
          : cell.row.original.finding.id;
      const link =
        `/orgs/${orgName}/groups/${cell.row.original.groupName}` +
        `/vulns/${findingId}/locations`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: t("searchFindings.tabVuln.vulnTable.vulnerabilityType.title"),
    size: 250,
  },
  {
    accessorKey: "where",
    enableColumnFilter: false,
    header: t("searchFindings.tabVuln.vulnTable.vulnerability"),
    size: 250,
  },
  {
    accessorFn: (): string => "View",
    cell: (cell): JSX.Element => {
      const orgName = cell.row.original.organizationName ?? "";
      const findingId =
        cell.row.original.finding === undefined
          ? ""
          : cell.row.original.finding.id;
      const link =
        `/orgs/${orgName}/groups/${cell.row.original.groupName}` +
        `/vulns/${findingId}/evidence`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    enableColumnFilter: false,
    header: "Evidence",
    size: 70,
  },
  {
    accessorFn: (row): string => row.verification ?? "-",
    header: t("searchFindings.tabVuln.vulnTable.verification"),
    id: "verification",
  },
  {
    accessorFn: (row): number => getSeverity(cvssVersion, row),
    cell: (cell): JSX.Element => severityFormatter(Number(cell.getValue())),
    header: cvssVersion.header,
    id: "severity",
  },
  {
    accessorKey: "tag",
    cell: (cell): JSX.Element => tagsFormatter(cell.getValue()),
    header: t("searchFindings.tabVuln.vulnTable.tags"),
  },
];

export {
  isRejectedOrSafeOrSubmitted,
  isVerificationRequestedOrOnHold,
  isValidVulnerability,
  filteredContinuousVulnerabilitiesOnReattackIds,
  getSeverity,
  getColumns,
};
